import { useEffect, useRef } from "react";
import { useAuth } from "@utils/firebaseAuth/auth";
import { trpc } from "@utils/trpc";

const HEARTBEAT_INTERVAL = 15_000;
const PRESENCE_STALE_AFTER_MS = 30_000;
const PRESENCE_EXPIRY_REFETCH_GRACE_MS = 500;

function createConnectionId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

export const useLiveRoomConnections = (
  accessId: string,
  shouldConnect: boolean = true,
) => {
  const { firebaseUser } = useAuth();
  const userId = firebaseUser?.uid;
  const { mutateAsync: joinLiveRoom } = trpc.user.liveRoom.join.useMutation();
  const { mutateAsync: heartbeatLiveRoom } =
    trpc.user.liveRoom.heartbeat.useMutation();
  const { mutateAsync: leaveLiveRoom } = trpc.user.liveRoom.leave.useMutation();
  const connections = trpc.user.liveRoom.getActiveConnections.useQuery(
    { accessId },
    { enabled: !!accessId && !!userId && shouldConnect },
  );
  const activeConnections = connections.data;
  const refetchConnections = connections.refetch;
  const connectionIdRef = useRef(createConnectionId());
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiryRefetchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!accessId || !userId || !shouldConnect) {
      return;
    }

    const connectionId = connectionIdRef.current;

    joinLiveRoom({ accessId, connectionId }).catch((error) => {
      console.error("live-room join failed", error);
    });

    heartbeatRef.current = setInterval(() => {
      heartbeatLiveRoom({ accessId, connectionId }).catch((error) => {
        console.error("live-room heartbeat failed", error);
      });
    }, HEARTBEAT_INTERVAL);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      leaveLiveRoom({ accessId, connectionId }).catch((error) => {
        console.error("live-room leave failed", error);
      });
    };
  }, [
    accessId,
    userId,
    shouldConnect,
    joinLiveRoom,
    heartbeatLiveRoom,
    leaveLiveRoom,
  ]);

  useEffect(() => {
    if (expiryRefetchRef.current) {
      clearTimeout(expiryRefetchRef.current);
      expiryRefetchRef.current = null;
    }

    if (!shouldConnect || !activeConnections?.length) return;

    const expiryTimes = activeConnections
      .map((connection) => Date.parse(connection.lastHeartbeatAt))
      .filter((heartbeatAt) => Number.isFinite(heartbeatAt))
      .map(
        (heartbeatAt) =>
          heartbeatAt +
          PRESENCE_STALE_AFTER_MS +
          PRESENCE_EXPIRY_REFETCH_GRACE_MS,
      );
    if (!expiryTimes.length) return;

    const delayMs = Math.max(1_000, Math.min(...expiryTimes) - Date.now());
    expiryRefetchRef.current = setTimeout(() => {
      expiryRefetchRef.current = null;
      refetchConnections().catch((error) => {
        console.error("live-room presence expiry refetch failed", error);
      });
    }, delayMs);

    return () => {
      if (expiryRefetchRef.current) {
        clearTimeout(expiryRefetchRef.current);
        expiryRefetchRef.current = null;
      }
    };
  }, [shouldConnect, activeConnections, refetchConnections]);

  return { connectionCount: connections.data?.length ?? 0 };
};
